import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ClientType,
  type CreateClientDetailsDto,
  CreateClientDetailsSchema,
  type EntityType,
} from '@nestjs-boilerplate/types';
import { useNavigate } from '@tanstack/react-router';
import { Info, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { COUNTRIES } from '@/constants/countries';
import { cn } from '@/lib/utils';

import {
  useClientDetails,
  useClientRefSearch,
  useSaveClientDetails,
  useSaveDraftClientDetails,
  useWizardAssessment,
} from '../api/risk-assessment-wizard';
import { MATTER_TYPE_OPTIONS } from '../constants/matter-types';

import { AddressFields } from './address-fields';
import { CompanyDetailsSection } from './company-details-section';
import { TitleMultiSelect } from './title-multi-select';
import { WizardFooter } from './wizard-footer';
import { WizardLayout } from './wizard-layout';

interface ClientDetailsFormProps {
  assessmentId: string;
}

const CLIENT_TYPE_OPTIONS: { value: ClientType; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'EXISTING', label: 'Existing' },
];

const ENTITY_TYPE_OPTIONS: { value: EntityType; label: string; disabled?: boolean }[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  // Organisation screening is not yet supported - only individuals are screened.
  { value: 'ORGANISATION', label: 'Organisation (coming soon)', disabled: true },
];

export function ClientDetailsForm({ assessmentId }: ClientDetailsFormProps) {
  const navigate = useNavigate();
  const { data: clientDetails, isLoading } = useClientDetails(assessmentId);
  const { data: assessment, isLoading: isLoadingAssessment } = useWizardAssessment(assessmentId);
  const saveClientDetailsMutation = useSaveClientDetails();
  const saveDraftMutation = useSaveDraftClientDetails();

  // State for re-run pre-population
  const [rerunSourceId, setRerunSourceId] = useState<string | null>(null);

  // Check for re-run source ID on mount
  useEffect(() => {
    const sourceId = localStorage.getItem('okyc_rerun_source_id');
    if (sourceId) {
      localStorage.removeItem('okyc_rerun_source_id');
      setRerunSourceId(sourceId);
    }
  }, []);

  // Fetch source assessment's client details for pre-population
  const { data: sourceClientDetails, isLoading: isLoadingSource } = useClientDetails(
    rerunSourceId ?? '',
  );

  const [clientRefSearch, setClientRefSearch] = useState('');
  const [debouncedClientRefSearch, setDebouncedClientRefSearch] = useState('');
  // Debounce client ref search - wait for user to stop typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClientRefSearch(clientRefSearch);
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [clientRefSearch]);

  // Search for existing client references (using debounced value)
  const { data: clientRefResults = [], isLoading: isSearchingClientRefs } =
    useClientRefSearch(debouncedClientRefSearch);

  const clientRefOptions = useMemo(
    () =>
      clientRefResults.map((result) => ({
        value: result.clientRef,
        label: result.clientRef,
        description: result.clientName,
        // Demo references are not the caller's own, so without the label a
        // reference they have never used appears in their picker unexplained.
        badge: result.isDemoFixture ? 'Demo' : undefined,
      })),
    [clientRefResults],
  );

  const form = useForm<CreateClientDetailsDto>({
    resolver: zodResolver(CreateClientDetailsSchema),
    defaultValues: {
      clientRef: '',
      clientType: 'NEW',
      entityType: 'INDIVIDUAL',
      isLegalMatter: false,
      matterType: undefined,
      titles: [],
      forename: '',
      middleName: '',
      surname: '',
      dateOfBirth: undefined,
      nationality: '',
      countryOfResidence: '',
      addressLine1: '',
      addressLine2: '',
      townCity: '',
      county: '',
      postcode: '',
      occupation: '',
      companies: [],
      consentGiven: false,
    },
  });

  // Track if we've already pre-populated to avoid re-running
  // Also use this as a key to force re-render of form components
  const [formKey, setFormKey] = useState(0);

  // Load existing client details (for continue/editing flow)
  useEffect(() => {
    // Only populate if we have client details and assessment and haven't already done so
    if (clientDetails && assessment && formKey === 0) {
      form.reset(
        {
          clientRef: clientDetails.clientRef ?? '',
          clientType: clientDetails.clientType ?? 'NEW',
          entityType: clientDetails.entityType ?? undefined,
          isLegalMatter: assessment.isLegalMatter ?? false,
          matterType: clientDetails.matterType ?? undefined,
          titles: clientDetails.titles ?? [],
          forename: clientDetails.forename ?? '',
          middleName: clientDetails.middleName ?? '',
          surname: clientDetails.surname ?? '',
          dateOfBirth: clientDetails.dateOfBirth ? new Date(clientDetails.dateOfBirth) : undefined,
          nationality: clientDetails.nationality ?? '',
          countryOfResidence: clientDetails.countryOfResidence ?? '',
          addressLine1: clientDetails.addressLine1 ?? '',
          addressLine2: clientDetails.addressLine2 ?? '',
          townCity: clientDetails.townCity ?? '',
          county: clientDetails.county ?? '',
          postcode: clientDetails.postcode ?? '',
          occupation: clientDetails.occupation ?? '',
          companies:
            clientDetails.companies?.map((c) => ({
              id: c.id,
              companyName: c.companyName,
              dateOfIncorporation: c.dateOfIncorporation ? new Date(c.dateOfIncorporation) : null,
              addressLine1: c.addressLine1 ?? '',
              addressLine2: c.addressLine2 ?? '',
              townCity: c.townCity ?? '',
              county: c.county ?? '',
              postcode: c.postcode ?? '',
              country: c.country ?? 'GB',
            })) ?? [],
          consentGiven: clientDetails.consentGiven ?? false,
        },
        { keepDefaultValues: false },
      );

      // Increment formKey to force re-render of Select components
      setFormKey((prev) => prev + 1);
    }
  }, [clientDetails, assessment, form, formKey]);

  // Pre-populate from source assessment (re-run flow) - separate effect to ensure it runs
  useEffect(() => {
    // Only pre-populate if:
    // 1. We have a source assessment ID (re-run flow)
    // 2. The current assessment has NO client details (it's new)
    // 3. We have fetched the source client details
    // 4. We haven't already pre-populated (formKey === 0 means first render)
    if (rerunSourceId && !clientDetails && sourceClientDetails && formKey === 0) {
      // Use form.reset() with all values to ensure proper form state update
      form.reset(
        {
          clientRef: '', // Clear - must be unique
          clientType: 'EXISTING', // Re-run means this is an existing client
          entityType: sourceClientDetails.entityType ?? undefined,
          isLegalMatter: false, // Reset - user should choose for this new assessment
          matterType: sourceClientDetails.matterType ?? undefined,
          titles: sourceClientDetails.titles ?? [],
          forename: sourceClientDetails.forename ?? '',
          middleName: sourceClientDetails.middleName ?? '',
          surname: sourceClientDetails.surname ?? '',
          dateOfBirth: sourceClientDetails.dateOfBirth
            ? new Date(sourceClientDetails.dateOfBirth)
            : undefined,
          nationality: sourceClientDetails.nationality ?? '',
          countryOfResidence: sourceClientDetails.countryOfResidence ?? '',
          addressLine1: sourceClientDetails.addressLine1 ?? '',
          addressLine2: sourceClientDetails.addressLine2 ?? '',
          townCity: sourceClientDetails.townCity ?? '',
          county: sourceClientDetails.county ?? '',
          postcode: sourceClientDetails.postcode ?? '',
          occupation: sourceClientDetails.occupation ?? '',
          companies:
            sourceClientDetails.companies?.map((c) => ({
              id: undefined, // Don't copy IDs - these will be new records
              companyName: c.companyName,
              dateOfIncorporation: c.dateOfIncorporation ? new Date(c.dateOfIncorporation) : null,
              addressLine1: c.addressLine1 ?? '',
              addressLine2: c.addressLine2 ?? '',
              townCity: c.townCity ?? '',
              county: c.county ?? '',
              postcode: c.postcode ?? '',
              country: c.country ?? 'GB',
            })) ?? [],
          consentGiven: false, // Reset - user must re-consent
        },
        { keepDefaultValues: false },
      );

      // Increment formKey to force re-render of Select components
      setFormKey((prev) => prev + 1);
    }
  }, [rerunSourceId, clientDetails, sourceClientDetails, form, formKey]);

  const handleSaveDraft = async () => {
    const data = form.getValues();
    await saveDraftMutation.mutateAsync({ assessmentId, data });
  };

  const handleCancel = () => {
    navigate({ to: '/' });
  };

  const handleNext = async (data: CreateClientDetailsDto) => {
    try {
      await saveClientDetailsMutation.mutateAsync({ assessmentId, data });
      // Navigate to list selection page (step 2)
      navigate({ to: '/risk-assessment/$id/list-selection', params: { id: assessmentId } });
    } catch {
      // Error toast is shown by mutation
    }
  };

  // Watch entity type to conditionally show company section
  const entityType = form.watch('entityType');

  if (isLoading || isLoadingAssessment || (rerunSourceId && isLoadingSource)) {
    return (
      <WizardLayout currentStep={1} completedSteps={[]}>
        <div className="flex h-full items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </WizardLayout>
    );
  }

  const isSaving = saveClientDetailsMutation.isPending || saveDraftMutation.isPending;
  const consentChecked = form.watch('consentGiven');

  return (
    <WizardLayout currentStep={1} completedSteps={[]}>
      <Form {...form} key={formKey}>
        <form onSubmit={form.handleSubmit(handleNext)} className="space-y-8 p-8">
          {/* Re-run Assessment Banner */}
          {rerunSourceId && (
            <div className="rounded-md bg-blue-50 p-4 text-sm text-blue-700">
              Client details have been pre-populated from a previous assessment. Please review and
              provide a new Client Reference before proceeding.
            </div>
          )}

          {/* Client Reference & Type Section */}
          <section className="space-y-6">
            {/* Client Reference with Autocomplete */}
            <FormField
              control={form.control}
              name="clientRef"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    Client Reference
                    <Tooltip>
                      <TooltipTrigger type="button">
                        <Info className="h-4 w-4 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Please insert your firms reference</p>
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <AutocompleteInput
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setClientRefSearch(value);
                      }}
                      options={clientRefOptions}
                      isLoading={isSearchingClientRefs}
                      placeholder="e.g., CLI001"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Client Type Toggle - Full Width */}
            <FormField
              control={form.control}
              name="clientType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Type</FormLabel>
                  <div className="flex gap-0">
                    {CLIENT_TYPE_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant="outline"
                        className={cn(
                          'h-10 flex-1 rounded-none first:rounded-l-md last:rounded-r-md',
                          field.value === option.value
                            ? 'bg-[#131313] text-white hover:bg-[#131313]/90 hover:text-white'
                            : 'bg-white text-[#131313] hover:bg-gray-50',
                        )}
                        onClick={() => field.onChange(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Entity Type */}
              <FormField
                control={form.control}
                name="entityType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Entity (Optional)
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <Info className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Select if this case relates to an individual or an organisation.</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select entity type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ENTITY_TYPE_OPTIONS.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Legal Matter (Optional) - Only shown when "I am a law firm" is checked */}
              {form.watch('isLegalMatter') && (
                <FormField
                  control={form.control}
                  name="matterType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        Legal Matter (Optional)
                        <Tooltip>
                          <TooltipTrigger type="button">
                            <Info className="h-4 w-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              What type of legal or financial matter is this risk assessment related
                              to?
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? ''}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select matter type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MATTER_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Law Firm Checkbox */}
            <FormField
              control={form.control}
              name="isLegalMatter"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="flex items-center gap-2">
                      I am a law firm in England & Wales
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <Info className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            If you are a law firm in England & Wales you have the option to generate
                            an SRA template report
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </section>

          <Separator />

          {/* Client Details Section */}
          <section>
            <h3 className="mb-6 text-[18px] font-semibold text-[#051923]">Client Details</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Titles (Multi-select dropdown with checkboxes) */}
              <TitleMultiSelect control={form.control} />

              {/* Forename */}
              <FormField
                control={form.control}
                name="forename"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">Forename</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Middle Name(s) */}
              <FormField
                control={form.control}
                name="middleName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Middle Name(s)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Middle names (Optional)"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Surname */}
              <FormField
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Surname</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Date of Birth */}
              <FormField
                control={form.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Date of Birth
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <Info className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Enter the client&apos;s full date of birth.</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <FormControl>
                      <DatePickerInput
                        value={field.value}
                        onChange={field.onChange}
                        disabled={(date: Date) => date > new Date()}
                        startMonth={new Date(1900, 0)}
                        endMonth={new Date()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Nationality */}
              <FormField
                control={form.control}
                name="nationality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Nationality
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <Info className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>As stated on the client&apos;s official identification documents.</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select nationality" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Country of Residence */}
              <FormField
                control={form.control}
                name="countryOfResidence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Country of Residence
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <Info className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>The country where the client currently resides.</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </section>

          <Separator />

          {/* Address Section */}
          <section>
            <h3 className="mb-6 text-[18px] font-semibold text-[#051923]">Address Information</h3>
            <AddressFields form={form} />
          </section>

          <Separator />

          {/* Occupation Section */}
          <section>
            <FormField
              control={form.control}
              name="occupation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    Occupation/Source of Income
                    <Tooltip>
                      <TooltipTrigger type="button">
                        <Info className="h-4 w-4 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Briefly describe the client&apos;s occupation and main source(s) of
                          income.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the client's occupation and source(s) of income..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>

          <Separator />

          {/* Company Details Section */}
          <CompanyDetailsSection form={form} entityType={entityType} />
        </form>

        <WizardFooter
          consentChecked={consentChecked}
          onConsentChange={(checked) => form.setValue('consentGiven', checked)}
          onSaveDraft={handleSaveDraft}
          onCancel={handleCancel}
          onNext={form.handleSubmit(handleNext)}
          isSaving={isSaving}
        />
      </Form>
    </WizardLayout>
  );
}
